import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate } from "../lib/utils";
import { DownloadSimple, FileText, Receipt, Info, Calendar } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";

export default function ClientInvoices() {
  const { t, lang } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/client/invoices").then((r) => r.data).catch(() => []),
      api.get("/client/proforma-invoices").then((r) => r.data).catch(() => []),
    ]).then(([inv, pf]) => {
      setInvoices(inv);
      setProformas(pf);
      setLoading(false);
    });
  }, []);

  const download = async (inv) => {
    try {
      const res = await api.get(`/client/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert(t("err_download_doc"));
    }
  };

  const downloadProforma = async (tx) => {
    try {
      const res = await api.get(`/client/proforma-invoices/${tx.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_n${tx.fic_proforma.number}_Teefarm.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert(t("err_download_doc"));
    }
  };

  const regimeLabel = (regime) => {
    if (regime === "eu_rc") return t("inv_regime_rc");
    if (regime === "extra_ue") return t("inv_regime_extraue");
    return t("inv_regime_it");
  };
  const regimeChip = (regime) => {
    if (regime === "eu_rc") return "tf-status-info";
    if (regime === "extra_ue") return "tf-status-warning";
    return "tf-status-success";
  };

  return (
    <div className="space-y-6" data-testid="client-invoices">
      <PageHeader
        eyebrow={t("invoices")}
        title={t("inv_page_title")}
        subtitle={t("inv_page_subtitle")}
        chip="tf-chip-cream"
        icon={FileText}
      />

      {/* --- COPIE DI CORTESIA FIC --- */}
      <div className="tf-card overflow-hidden" data-testid="fic-proforma-card">
        <div className="p-5 pb-3 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--tf-border)]">
          <div>
            <div className="tf-label flex items-center gap-2"><Receipt size={12} weight="fill" /> {t("inv_courtesy_title")}</div>
            <div className="font-display font-bold text-lg mt-1">{t("inv_courtesy_name")}</div>
            <div className="text-xs text-[var(--tf-muted)] mt-1">
              {t("inv_courtesy_desc")}
            </div>
          </div>
        </div>
        {/* Unified feed (mobile + desktop) — each row has Download PDF button on the right */}
        <div className="divide-y divide-[var(--tf-border)]" data-testid="proforma-feed">
          {loading && <div className="text-center text-[var(--tf-muted)] py-10 text-sm">{t("loading")}</div>}
          {!loading && proformas.length === 0 && (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-[#F5F2E8] text-[var(--tf-ink)] flex items-center justify-center mx-auto mb-3">
                <Receipt size={24} weight="fill" />
              </div>
              <div className="font-display font-bold text-sm">{t("inv_no_proforma")}</div>
              <div className="text-xs text-[var(--tf-muted)] mt-1">{t("inv_no_proforma_desc")}</div>
            </div>
          )}
          {proformas.map((tx) => (
            <div
              key={tx.id}
              className="flex items-center gap-3 md:gap-4 p-4 md:px-5 hover:bg-[var(--tf-bg)]/40 transition-colors"
              data-testid={`proforma-${tx.id}`}
            >
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-[#F5F2E8] text-[var(--tf-ink)] flex items-center justify-center flex-shrink-0">
                <Receipt size={20} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-sm truncate">Proforma n.{tx.fic_proforma.number}</div>
                  <span className={`tf-status-pill ${regimeChip(tx.fic_proforma.regime)} text-[9px]`}>
                    {regimeLabel(tx.fic_proforma.regime)}
                  </span>
                </div>
                <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <Calendar size={11} weight="bold" />
                  <span>{fmtDate(tx.created_at, lang)}</span>
                  <span className="opacity-40 hidden md:inline">·</span>
                  <span className="hidden md:inline truncate">{tx.description}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                <div className="font-display font-black text-sm md:text-base tf-numeric text-[var(--tf-ink)] text-right" style={{ textShadow: "none" }}>
                  {fmtEur(tx.amount)}
                </div>
                {tx.fic_proforma.pdf_url ? (
                  <button
                    onClick={() => downloadProforma(tx)}
                    className="w-10 h-10 rounded-2xl bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] text-[var(--tf-ink)] flex items-center justify-center transition-all active:scale-95 flex-shrink-0"
                    data-testid={`fic-download-${tx.id}`}
                    title={t("download_pdf")}
                    aria-label={t("download_pdf")}
                  >
                    <DownloadSimple size={16} weight="bold" />
                  </button>
                ) : (
                  <span className="text-[9px] text-[var(--tf-muted)] uppercase tracking-wider">{t("inv_in_queue")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Nota legale */}
        <div className="p-4 border-t border-[var(--tf-border)] bg-[#F5F2E8] flex items-start gap-2">
          <Info size={14} weight="fill" className="text-[var(--tf-ink)] mt-0.5 flex-shrink-0" />
          <div className="text-[11px] text-[var(--tf-muted)] leading-relaxed">
            {t("inv_legal_note")}
          </div>
        </div>
      </div>

      {/* --- FATTURE INTERNE (storico legacy) --- */}
      {invoices.length > 0 && (
        <div className="tf-card overflow-hidden" data-testid="legacy-invoices-card">
          <div className="p-5 pb-3 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--tf-border)]">
            <div>
              <div className="tf-label flex items-center gap-2"><FileText size={12} weight="fill" /> {t("inv_archive_title")}</div>
              <div className="font-display font-bold text-lg mt-1">{t("inv_archive_name")}</div>
              <div className="text-xs text-[var(--tf-muted)] mt-1">{t("inv_numbering", { year: new Date().getFullYear() })}</div>
            </div>
          </div>
          <div className="divide-y divide-[var(--tf-border)]" data-testid="invoices-feed">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center gap-3 md:gap-4 p-4 md:px-5 hover:bg-[var(--tf-bg)]/40 transition-colors"
                data-testid={`invoice-${inv.id}`}
              >
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-[#DAECFA] text-[#1F5A93] flex items-center justify-center flex-shrink-0">
                  <FileText size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-sm truncate">{inv.number}</div>
                    <span className="tf-status-pill tf-status-info text-[9px]">{inv.method === "stripe" ? "Stripe" : t("inv_method_bank_transfer")}</span>
                  </div>
                  <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 flex items-center gap-1.5">
                    <Calendar size={11} weight="bold" />
                    <span>{fmtDate(inv.issued_at, lang)}</span>
                    <span className="opacity-40 hidden md:inline">·</span>
                    <span className="hidden md:inline truncate">{inv.description}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                  <div className="font-display font-black text-sm md:text-base tf-numeric text-[var(--tf-ink)]">{fmtEur(inv.amount)}</div>
                  <button
                    onClick={() => download(inv)}
                    className="w-10 h-10 rounded-2xl bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] text-[var(--tf-ink)] flex items-center justify-center transition-all active:scale-95"
                    data-testid={`download-${inv.id}`}
                    title={t("download_pdf")}
                    aria-label={t("download_pdf")}
                  >
                    <DownloadSimple size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
